// src/assets/variant-cards.js
// Reiner Builder für Variant-Karten (Ghost-Cards) — TODO 1 + 14.
// Keine DOM-Abhängigkeit; Tests via node:test. Browser-Bridge unten.

const OPENED_VALUES = [
  'geprüfte retoure', 'gepruefte retoure', 'inspected return',
  'geöffnete verpackung', 'geoeffnete verpackung', 'opened packaging', 'open box',
];

export function normalizeCondition(raw) {
  const v = String(raw == null ? '' : raw).trim().toLowerCase();
  return OPENED_VALUES.includes(v) ? 'opened' : 'new';
}

// Liquid liefert `conditionKey` ('opened'|'new') mit — sprachunabhängig.
// normalizeCondition bleibt als Fallback für Karten aus ungepatchten Templates.
export function conditionKeyOf(v) {
  const key = v && v.conditionKey;
  return key === 'opened' || key === 'new' ? key : normalizeCondition(v && v.condition);
}

const OPENED_LABEL = 'Geprüfte Retoure';

function eq(a, b) {
  return String(a == null ? '' : a).trim().toLowerCase() ===
         String(b == null ? '' : b).trim().toLowerCase();
}

export function buildVariantCards(variants, options = {}) {
  const wantSize = options.size;
  const inScope = (variants || []).filter(v => !wantSize || eq(v.size, wantSize));

  const make = (v) => {
    const cond = conditionKeyOf(v);
    return {
      variantId: v.variantId, color: v.color, size: v.size,
      condition: cond, isOpened: cond === 'opened',
      price: v.price, compareAt: v.compareAt, available: v.available,
      auslaufend: v.auslaufend === true,
      image: v.image, url: v.url,
      // Der Optionswert IST der übersetzte Anzeigetext — kein Locale-Key nötig.
      label: cond === 'opened' ? (v.condition || OPENED_LABEL) : '',
    };
  };

  const pickPerColor = (cards) => {
    const seen = new Set();
    const out = [];
    for (const c of cards) {
      // When filtering by size, deduplicate by color only.
      // When showing all sizes, deduplicate by (color, size) so each size appears.
      const key = wantSize
        ? String(c.color || '').toLowerCase()
        : `${String(c.color || '').toLowerCase()}|${String(c.size || '').toLowerCase()}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(c);
    }
    return out;
  };

  const made = inScope.map(make);
  const newCards = pickPerColor(made.filter(c => c.condition === 'new'));
  const openedCards = pickPerColor(made.filter(c => c.condition === 'opened' && c.available));
  return [...newCards, ...openedCards];
}

export function filterSonderpreis(cards, onlyOpened) {
  return onlyOpened ? (cards || []).filter(c => c.isOpened) : (cards || []);
}

// Filtert Karten auf die aktive Farb-Facette (filter.v.option.farbe).
// Leerer/kein Filter → unverändert. Vergleich case-insensitiv.
export function filterColors(cards, colors) {
  const wanted = (colors || [])
    .map(c => String(c == null ? '' : c).trim().toLowerCase())
    .filter(Boolean);
  if (!wanted.length) return cards || [];
  const set = new Set(wanted);
  return (cards || []).filter(
    c => set.has(String(c.color == null ? '' : c.color).trim().toLowerCase())
  );
}

// Geldformat passend zum Storefront (€83,98). Eingabe in Cent (Shopify v.price).
export function formatMoney(cents) {
  const n = Number(cents || 0) / 100;
  return '€' + n.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// Baut den .price-Innenraum für EINE Ghost-Card (exakter Variantenpreis statt
// "Ab"-Range der Basis-Card). Spiegelt die Struktur aus snippets/price.liquid.
// taxSuffix: optionales bereits gerendertes <span class="sup">…</span> (inkl. MwSt.).
export function priceHtml(card, taxSuffix = '') {
  const onSale = card.compareAt != null && Number(card.compareAt) > Number(card.price);
  const cur = formatMoney(card.price) + taxSuffix;
  let inner = '<div class="price-container">'
    + '<div class="price-regular"><span class="price-item price-item-regular">' + cur + '</span></div>';
  if (onSale) {
    inner += '<div class="price-sale">'
      + '<span class="price-discount"><s class="price-item price-item-regular">'
      + formatMoney(card.compareAt) + '</s>'
      + saleBadgeHtml(card)
      + '</span>'
      + '<span class="price-item price-item-sale price-item-last">' + cur + '</span>'
      + '</div>';
  }
  inner += '</div>';
  return inner;
}

// Sale-Badge neben dem Strichpreis — spiegelt snippets/badge-sale.liquid
// (Prozent- vs. "Angebot"-Text über settings.sale_badge_mode, gated auf
// != 'none'). Der Theme-Setting-Wert ist im Browser nicht direkt greifbar
// (kein `settings`-Objekt im JS) → sections/filters.liquid reicht ihn als
// window.cecLocale.sale_badge_mode / .on_sale durch (gleicher Mechanismus wie
// die Stock-Badge-Strings oben). Nur aufgerufen wenn card bereits onSale ist.
function saleBadgeHtml(card) {
  const mode = localeText('sale_badge_mode', 'sale');
  if (mode === 'none') return '';
  let text;
  if (mode === 'percentage') {
    const pct = Math.round(
      ((Number(card.compareAt) - Number(card.price)) / Number(card.compareAt)) * 100
    );
    text = '-' + pct + '%';
  } else {
    text = localeText('on_sale', 'Angebot');
  }
  return '<span class="badge badge-sale">' + escapeHtml(text) + '</span>';
}

// Strings aus window.cecLocale (gefüllt in sections/filters.liquid), gleicher
// Fallback-Mechanismus wie t() in collection-handle-filter.js. Kein window()
// unter node:test → Fallback greift dort immer.
// Dupliziert statt über die Bridge geteilt: variant-cards.js ist ES-Modul,
// collection-handle-filter.js ein bundlerloses IIFE mit eigener, PRE-EXISTING
// t()/escapeHtml()-Kopie, die dort schon vor dieser Änderung unabhängig
// benutzt wird — Umbiegen auf window.CECVariantCards würde unrelated Code
// anfassen, ohne Verhalten zu ändern. Absichtlich getrennt gehalten.
// fallow-ignore-next-line code-duplication
function localeText(key, fallback) {
  return (typeof window !== 'undefined' && window.cecLocale && window.cecLocale[key]) || fallback;
}

// fallow-ignore-next-line code-duplication
function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, c => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  }[c]));
}

// Zustände für den Stock-Badge einer Ghost-Card. `warning` (Auslaufend) GEWINNT
// unabhängig von `available` — das repliziert snippets/product-availability.liquid
// Zeile 26+48 ("Auslaufend ... gewinnt vor allen anderen Status (gelbes Badge
// statt grün/rot)"): is_discontinued wird dort VOR jeder Verfügbarkeitsprüfung
// abgefragt, eine auslaufende Variante zeigt "Auslaufend" auch wenn sie selbst
// 0 Bestand hat. product-availability-listing.liquid (Karten-Aggregat) kommt
// beim einzelnen ausverkauften, geflaggten Varianten-Fall auf dasselbe Ergebnis
// (has_discontinued && has_regular_stock == false → Auslaufend statt Ausverkauft).
const STOCK_STATE = {
  in: { cls: 'badge-stock-in', key: 'availability_in_stock', fallback: 'Vorrätig' },
  out: { cls: 'badge-stock-out', key: 'availability_out_of_stock', fallback: 'Nicht vorrätig' },
  warning: { cls: 'badge-stock-warning', key: 'availability_discontinued', fallback: 'Auslaufend' },
};

// Patcht den serverseitig gerenderten Stock-Badge (snippets/product-availability-listing.liquid,
// `.badge-stock`) einer geklonten Ghost-Card auf den Zustand der konkret
// gematchten Größen-Variante. Ohne den Patch zeigt die Ghost-Card weiter die
// Produkt-Level-Verfügbarkeit (irgendeine Variante lagernd), obwohl genau die
// vom Kunden gesuchte Größe ausverkauft oder auslaufend sein kann.
//
// Idempotent: className/innerHTML werden bei jedem Aufruf komplett neu
// aufgebaut statt angehängt — mehrfaches apply() (Facet-Grid-Swap) verändert
// den Endzustand nicht und verschachtelt kein Markup.
// fallow-ignore-next-line unused-export, complexity
export function applyVariantStock(clone, card) {
  if (!clone || typeof clone.querySelector !== 'function') return;
  if (card == null || card.available == null) return;
  const el = clone.querySelector('.badge-stock');
  if (!el) return;

  const stateKey = card.auslaufend === true ? 'warning' : (card.available === true ? 'in' : 'out');
  const state = STOCK_STATE[stateKey];

  const cls = String(el.className || '').split(/\s+/)
    .filter(c => c && !/^badge-stock-/.test(c) && c !== 'badge-backorder' && c !== 'badge-preorder');
  cls.push(state.cls);
  el.className = cls.join(' ');

  el.innerHTML = '<span class="badge-stock-icon"></span> ' + escapeHtml(localeText(state.key, state.fallback));
}

// Browser-Bridge: collection-handle-filter.js (IIFE) nutzt window.CECVariantCards.
if (typeof window !== 'undefined') {
  window.CECVariantCards = {
    normalizeCondition, buildVariantCards, filterSonderpreis,
    filterColors, formatMoney, priceHtml, applyVariantStock,
  };
}
